import React, { useCallback } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";

import { DatePicker, IDatePickerProps } from "../input/DatePicker";
import { FieldPathOfType, IControllerProps } from "./formTypes";

type IControlledDatePickerProps = Omit<IDatePickerProps, "value" | "inputRef">;

interface IProps<TFieldValues extends FieldValues, TName extends FieldPathOfType<TFieldValues, Date>>
    extends IControlledDatePickerProps {
    name: TName;
    controllerProps?: IControllerProps<TFieldValues, TName, Date>;
}

export function ControllerDatePicker<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPathOfType<TFieldValues, Date> = FieldPathOfType<TFieldValues, Date>,
>({
    name,
    controllerProps,
    onChange,
    onBlur,
    ...datePickerProps
}: IProps<TFieldValues, TName>): React.ReactElement | null {
    const {
        field: { value, onChange: onFieldChange, onBlur: onFieldBlur, ref },
    } = useController({ ...controllerProps, name });

    const { trigger } = useFormContext();

    const handleChange = useCallback(
        (date: Date | undefined): void => {
            onFieldChange(date ?? null);
            onChange?.(date);
            trigger(name);
        },
        [onFieldChange, onChange, trigger, name],
    );
    const handleBlur = useCallback<React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>>(
        (...args) => {
            onFieldBlur();
            onBlur?.(...args);
        },
        [onFieldBlur, onBlur],
    );

    return (
        <MemoizedDatePicker
            {...datePickerProps}
            inputRef={ref}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
        />
    );
}

const MemoizedDatePicker = React.memo(DatePicker);
